import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { FileText, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';
const token = () => localStorage.getItem('token');
const hdrs  = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });

export default function IntroducerProfile() {
  const { introducerId, userId } = useParams();
  const profileId = userId || introducerId;
  const isUserBased = !!userId;
  const navigate = useNavigate();
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [editModal,  setEditModal]  = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [saveMsg,    setSaveMsg]    = useState('');
  const [pdfLoading,    setPdfLoading]    = useState(false);
  const [idUploading,   setIdUploading]   = useState(false);
  const [idError,       setIdError]       = useState('');
  const [idSuccess,     setIdSuccess]     = useState('');
  const [dlFile,        setDlFile]        = useState(null);
  const [ppFile,        setPpFile]        = useState(null);
  const [agrTab,        setAgrTab]        = useState('personal');
  const [showReplaceForm, setShowReplaceForm] = useState(false);

  const [editForm, setEditForm] = useState({
    fee_type: 'percentage', fee_value: '', fee_applies_to_renewals: false,
    adviser_name: '', frn_number: '', name: '', email: '', contact_phone: '',
    entity_type: 'individual', company_name: '', company_address: ''
  });

  // Relationship notes (internal to the broker — never visible to the introducer)
  const [notes,      setNotes]      = useState([]);
  const [newNote,    setNewNote]    = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError,  setNoteError]  = useState('');

  const loadNotes = async () => {
    try {
      const res = await fetch(`${API}/api/introducers/${profileId}/notes`, { headers: hdrs() });
      if (!res.ok) return;
      const json = await res.json();
      setNotes(json.notes || []);
    } catch { /* non-fatal */ }
  };

  const addNote = async () => {
    const body = newNote.trim();
    if (!body) return;
    setNoteSaving(true); setNoteError('');
    try {
      const res = await fetch(`${API}/api/introducers/${profileId}/notes`, {
        method: 'POST', headers: hdrs(), body: JSON.stringify({ body }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to save note');
      setNotes(n => [json.note, ...n]);
      setNewNote('');
    } catch (err) {
      setNoteError(err.message);
    } finally {
      setNoteSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const res = await fetch(`${API}/api/introducers/${profileId}/notes/${noteId}`, { method: 'DELETE', headers: hdrs() });
      if (res.ok) setNotes(n => n.filter(x => x.id !== noteId));
    } catch { /* ignore */ }
  };

  const load = async () => {
    try {
      const endpoint = isUserBased ? `${API}/api/introducers/by-user/${profileId}` : `${API}/api/introducers/${profileId}`;
      const res  = await fetch(endpoint, { headers: hdrs() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
      const inv = json.invite;
      setEditForm({
        fee_type:               inv?.fee_type || 'percentage',
        fee_value:              inv?.fee_value || '',
        fee_applies_to_renewals: inv?.fee_applies_to_renewals || false,
        adviser_name:           inv?.adviser_name || '',
        frn_number:             inv?.frn_number || '',
        name:                   json.introducer?.name || '',
        email:                  json.introducer?.email || '',
        contact_phone:          inv?.contact_phone || '',
        entity_type:            json.introducer?.entity_type || 'individual',
        company_name:           json.introducer?.company_name || '',
        company_address:        json.introducer?.company_address || '',
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadNotes(); }, [introducerId]);

  const handleSave = async () => {
    if (editForm.entity_type === 'company' && !editForm.company_name.trim()) {
      setSaveMsg('Company name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/introducers/${profileId}`, {
        method: 'PATCH', headers: hdrs(), body: JSON.stringify(editForm)
      });
      if (!res.ok) throw new Error('Save failed');
      setSaveMsg('Saved ✓');
      setTimeout(() => setSaveMsg(''), 2000);
      setEditModal(false);
      load();
    } catch (err) {
      setSaveMsg('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusToggle = async () => {
    const newStatus = data.introducer.status === 'active' ? 'suspended' : 'active';
    if (!window.confirm(`${newStatus === 'suspended' ? 'Suspend' : 'Reactivate'} this introducer?`)) return;
    await fetch(`${API}/api/introducers/${profileId}`, {
      method: 'PATCH', headers: hdrs(), body: JSON.stringify({ status: newStatus })
    });
    load();
  };

  const handleDelete = async () => {
    await fetch(`${API}/api/introducers/${profileId}`, { method: 'DELETE', headers: hdrs() });
    navigate('/introducers');
  };

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const res = await fetch(`${API}/api/agreement-review/${profileId}/pdf`, { headers: hdrs() });
      if (!res.ok) { alert('PDF not yet generated.'); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `Agreement_${data?.introducer?.name?.replace(/[^a-zA-Z0-9]/g,'_') || profileId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert('Download failed'); }
    finally { setPdfLoading(false); }
  };

  const generatePDF = async () => {
    setPdfLoading(true);
    try {
      const res = await fetch(`${API}/api/agreement-review/${profileId}/generate-pdf`, {
        method: 'POST', headers: hdrs()
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Generation failed');
      await load();
    } catch (err) { alert('PDF generation failed: ' + err.message); }
    finally { setPdfLoading(false); }
  };

  const uploadID = async () => {
    if (!dlFile && !ppFile) return setIdError('Please select at least one document');
    setIdUploading(true); setIdError(''); setIdSuccess('');
    try {
      const fd = new FormData();
      if (dlFile) fd.append('driving_licence', dlFile);
      if (ppFile) fd.append('passport', ppFile);
      const res = await fetch(`${API}/api/agreements/upload-id-broker/${profileId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Upload failed');
      setIdSuccess('Documents uploaded successfully ✓');
      setDlFile(null); setPpFile(null);
      setShowReplaceForm(false);
      load();
    } catch (err) { setIdError(err.message); }
    finally { setIdUploading(false); }
  };

  const safeParse = (val) => {
    if (!val) return null;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch { return null; }
  };

  const safeNum = v => parseFloat(v) || 0;

  if (loading) return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F7FAFC' }}>
      <Sidebar />
      <main className="main-content" style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
        <p style={{ color:'#94a3b8' }}>Loading...</p>
      </main>
    </div>
  );

  if (error) return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F7FAFC' }}>
      <Sidebar />
      <main className="main-content" style={{ padding:32 }}>
        <p style={{ color:'#dc2626' }}>{error}</p>
        <button onClick={() => navigate(-1)} style={btn('#f1f5f9','#374151')}>← Back</button>
      </main>
    </div>
  );

  const intro  = data.introducer;
  const stats  = data.stats;
  const deals  = data.deals || [];
  const inv    = data.invite || {};
  const agr    = data.agreement || null;
  const isSuspended = intro.status === 'suspended';
  const isArchived  = intro.status === 'archived';

  // Agreement data parsing
  const dynamicFormData   = safeParse(agr?.dynamic_form_data) || null;
  const hasCustomTemplate = !!(agr?.template_id && dynamicFormData);
  const oldDeclarations   = safeParse(agr?.declaration_answers) || [];
  const newDeclarations   = (() => {
    if (!hasCustomTemplate || oldDeclarations.length > 0) return [];
    const templateFields = safeParse(agr?.template_extracted_fields) || [];
    return templateFields
      .filter(f => f.type === 'select' && f.options?.includes('Yes') && f.options?.includes('No'))
      .map(f => ({
        question: f.label,
        answer:   dynamicFormData[f.key] === 'Yes',
        details:  dynamicFormData['additional_details'] || dynamicFormData['additional_information'] || ''
      }));
  })();
  const allDeclarations = oldDeclarations.length > 0 ? oldDeclarations : newDeclarations;
  const yesDeclarations = allDeclarations.filter(d => d.answer === true || d.answer === 'Yes');
  const introDetails    = safeParse(agr?.introducer_details) || {};
  const firmDetails     = safeParse(agr?.firm_details) || {};

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#F7FAFC' }}>
      <Sidebar />
      <main className="main-content" style={{ padding:'24px 20px', maxWidth:960 }}>

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:24 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <button onClick={() => navigate(-1)} style={btn('#fff','#374151', '1px solid #e2e8f0')}>← Back</button>
            <div>
              <h1 style={{ margin:'0 0 2px', fontSize:20, fontWeight:700, color:'#0e1b2c' }}>{intro.name}</h1>
              <p style={{ margin:0, fontSize:13, color:'#718096' }}>{intro.contact_email || intro.email}</p>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
            <span style={{
              fontSize:12, fontWeight:600, padding:'4px 12px', borderRadius:20,
              background: isSuspended ? '#fef3c7' : isArchived ? '#f1f5f9' : '#d1fae5',
              color: isSuspended ? '#92400e' : isArchived ? '#64748b' : '#065f46',
            }}>
              {isSuspended ? '⏸ Suspended' : isArchived ? '🗃 Archived' : '✅ Active'}
            </span>
          </div>
        </div>

        {/* ── Action bar ──────────────────────────────────────────────── */}
        {!isArchived && (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:24 }}>
            <button onClick={() => setEditModal(true)} style={btn('#10b981','#fff')}>✏️ Edit Details</button>
            <button onClick={handleStatusToggle}
              style={btn(isSuspended ? '#d1fae5' : '#fef3c7', isSuspended ? '#065f46' : '#92400e')}>
              {isSuspended ? '▶ Reactivate' : '⏸ Suspend'}
            </button>
            {agr?.agreement_pdf_path ? (
              <button onClick={downloadPDF} disabled={pdfLoading} style={btn('#eff6ff','#1d4ed8')}>
                {pdfLoading ? 'Downloading...' : '📄 Download Agreement'}
              </button>
            ) : agr && (
              <button onClick={generatePDF} disabled={pdfLoading} style={btn('#f0fdf4','#166534','1px solid #86efac')}>
                {pdfLoading ? 'Generating...' : '⚙️ Generate PDF'}
              </button>
            )}
            <button onClick={() => setConfirmDel(true)} style={btn('#fef2f2','#dc2626')}>🗑 Archive</button>
          </div>
        )}

        {/* ── Stats cards ─────────────────────────────────────────────── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:12, marginBottom:24 }}>
          {[
            ['Total Deals',    stats.totalDeals,    `${stats.completedDeals} completed`],
            ['Pipeline',       stats.pipelineDeals, 'In progress'],
            ['Commission',     `£${safeNum(stats.totalCommission).toFixed(2)}`, 'All time'],
            ['Avg Deal',       `£${stats.totalDeals > 0 ? (safeNum(stats.totalCommission)/stats.totalDeals).toFixed(2) : '0.00'}`, 'Per deal'],
          ].map(([label, value, detail]) => (
            <div key={label} style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'16px 20px' }}>
              <p style={{ margin:'0 0 4px', fontSize:11, fontWeight:600, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</p>
              <p style={{ margin:'0 0 2px', fontSize:22, fontWeight:700, color:'#0e1b2c' }}>{value}</p>
              <p style={{ margin:0, fontSize:11, color:'#94a3b8' }}>{detail}</p>
            </div>
          ))}
        </div>

        {/* ── Top info row ─────────────────────────────────────────────── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16, marginBottom:24 }}>

          {/* Introducer details */}
          <div style={card}>
            <h3 style={cardTitle}>Introducer Details</h3>
            {[
              ['Name',    intro.name],
              ['Email',   intro.contact_email || intro.email],
              ['Phone',   intro.contact_phone || '—'],
              ['Type',    intro.entity_type === 'company' ? 'Company' : 'Individual'],
              ...(intro.entity_type === 'company' ? [
                ['Company Name',    intro.company_name || '—'],
                ['Company Address', intro.company_address || '—'],
              ] : []),
              ['Status',  intro.status],
              ['Joined',  intro.created_at ? new Date(intro.created_at).toLocaleDateString('en-GB') : '—'],
            ].map(([l,v]) => <InfoRow key={l} label={l} value={v} />)}
          </div>

          {/* Fee & adviser */}
          <div style={card}>
            <h3 style={cardTitle}>Fee Arrangement</h3>
            {[
              ['Fee Type',  inv.fee_type === 'fixed' ? 'Fixed Fee' : 'Percentage'],
              ['Fee Value', inv.fee_type === 'fixed' ? `£${safeNum(inv.fee_value).toFixed(2)}` : `${safeNum(inv.fee_value)}%`],
              ['Renewals',  inv.fee_applies_to_renewals ? 'Yes' : 'No'],
              ['Adviser',   inv.adviser_name || '—'],
              ['FRN',       inv.frn_number || '—'],
            ].map(([l,v]) => <InfoRow key={l} label={l} value={v} />)}
          </div>

          {/* Agreement summary */}
          <div style={card}>
            <h3 style={cardTitle}>Agreement Summary</h3>
            {agr ? (
              <>
                {[
                  ['Status',    agr.status],
                  ['Signed',    agr.signed_at ? new Date(agr.signed_at).toLocaleString('en-GB') : '—'],
                  ['Signed as', agr.esign_name || '—'],
                  ['IP',        agr.ip_address || '—'],
                  ['ID Docs',   agr.id_upload_skipped ? '⚠️ Pending' : (agr.driving_licence_path || agr.passport_path) ? '✅ Uploaded' : '⚠️ Pending'],
                ].map(([l,v]) => <InfoRow key={l} label={l} value={v} />)}
              </>
            ) : (
              <p style={{ fontSize:13, color:'#94a3b8' }}>No agreement signed yet.</p>
            )}
          </div>
        </div>

        {/* ── Full Agreement Detail ────────────────────────────────────── */}
        {agr && (
          <div style={{ ...card, marginBottom:24, padding:0, overflow:'hidden' }}>
            <div style={{ padding:'16px 20px', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <h3 style={{ ...cardTitle, margin:0 }}>Agreement Details</h3>
              {agr.agreement_pdf_path ? (
                <button onClick={downloadPDF} disabled={pdfLoading} style={btn('#eff6ff','#1d4ed8','none','6px 14px','12px')}>
                  {pdfLoading ? 'Downloading...' : '📄 Download PDF'}
                </button>
              ) : (
                <button onClick={generatePDF} disabled={pdfLoading} style={btn('#f0fdf4','#166534','1px solid #86efac','6px 14px','12px')}>
                  {pdfLoading ? 'Generating...' : '⚙️ Generate PDF'}
                </button>
              )}
            </div>

            {/* Tabs */}
            <div style={{ display:'flex', borderBottom:'1px solid #e2e8f0', padding:'0 20px', background:'#fafafa' }}>
              {[
                ['personal', 'Personal'],
                ['firm', 'Firm'],
                ['declarations', `Declarations${yesDeclarations.length > 0 ? ` (${yesDeclarations.length} YES)` : ''}`],
                ['id', 'ID & Docs'],
              ].map(([key, label]) => (
                <button key={key} onClick={() => setAgrTab(key)} style={{
                  padding:'10px 16px', border:'none', background:'none', cursor:'pointer',
                  fontSize:13, color: agrTab === key ? '#10b981' : '#6b7280',
                  borderBottom: agrTab === key ? '2px solid #10b981' : '2px solid transparent',
                  fontWeight: agrTab === key ? 600 : 400, marginBottom:-1,
                }}>
                  {label}
                </button>
              ))}
            </div>

            <div style={{ padding:20 }}>

              {/* Personal tab */}
              {agrTab === 'personal' && (
                <div>
                  {hasCustomTemplate ? (
                    <DynamicDataView formData={dynamicFormData} agreement={agr} />
                  ) : (
                    <>
                      <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'12px 16px', marginBottom:16, display:'flex', gap:24, flexWrap:'wrap' }}>
                        <div>
                          <p style={{ fontSize:11, color:'#9ca3af', margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>Signed As</p>
                          <p style={{ fontSize:15, fontStyle:'italic', color:'#1a365d', fontWeight:600, margin:0 }}>{agr.esign_name}</p>
                        </div>
                        <div>
                          <p style={{ fontSize:11, color:'#9ca3af', margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>Date</p>
                          <p style={{ fontSize:13, color:'#065f46', margin:0 }}>{new Date(agr.signed_at).toLocaleString('en-GB')}</p>
                        </div>
                        <div>
                          <p style={{ fontSize:11, color:'#9ca3af', margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>IP Address</p>
                          <p style={{ fontSize:13, color:'#065f46', margin:0 }}>{agr.ip_address}</p>
                        </div>
                      </div>
                      {[
                        ['Full Name',     introDetails.name],
                        ['Date of Birth', introDetails.dob],
                        ['Address',       introDetails.address],
                        ['Telephone',     introDetails.phone],
                        ['Mobile',        introDetails.mobile],
                        ['Email',         introDetails.email],
                      ].map(([l,v]) => <InfoRow key={l} label={l} value={v} />)}
                    </>
                  )}
                </div>
              )}

              {/* Firm tab */}
              {agrTab === 'firm' && (
                <div>
                  {hasCustomTemplate ? (
                    <DynamicFirmView formData={dynamicFormData} />
                  ) : (
                    [
                      ['Firm Name',          firmDetails.firm_name],
                      ['Firm Address',       firmDetails.firm_address],
                      ['Telephone',          firmDetails.phone],
                      ['Nature of Business', firmDetails.nature_of_business],
                    ].map(([l,v]) => <InfoRow key={l} label={l} value={v} />)
                  )}
                </div>
              )}

              {/* Declarations tab */}
              {agrTab === 'declarations' && (
                <div>
                  {yesDeclarations.length > 0 && (
                    <div style={{ display:'flex', alignItems:'center', gap:8, background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#92400e', marginBottom:16 }}>
                      <AlertTriangle size={16} />
                      {yesDeclarations.length} question{yesDeclarations.length > 1 ? 's' : ''} answered YES — review carefully
                    </div>
                  )}
                  {allDeclarations.length === 0 && (
                    <p style={{ fontSize:13, color:'#9ca3af' }}>No declaration answers recorded.</p>
                  )}
                  {allDeclarations.map((d, i) => {
                    const isYes = d.answer === true || d.answer === 'Yes';
                    return (
                      <div key={i} style={{ padding:'10px', borderRadius: isYes ? 6 : 0, marginBottom:4,
                        background: isYes ? '#fef2f2' : 'transparent',
                        borderBottom: isYes ? 'none' : '1px solid #f3f4f6',
                        display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                        <span style={{ fontSize:13, color:'#374151', flex:1 }}>{d.question}</span>
                        <span style={{ fontSize:13, fontWeight:700, minWidth:36, textAlign:'right', color: isYes ? '#dc2626' : '#16a34a' }}>
                          {isYes ? 'YES' : 'NO'}
                        </span>
                      </div>
                    );
                  })}
                  {allDeclarations[0]?.details && (
                    <div style={{ background:'#f9fafb', borderRadius:8, padding:'12px 14px', marginTop:16 }}>
                      <p style={{ fontSize:11, color:'#9ca3af', fontWeight:600, textTransform:'uppercase', margin:'0 0 6px' }}>Additional Details</p>
                      <p style={{ fontSize:13, color:'#374151', margin:0 }}>{allDeclarations[0].details}</p>
                    </div>
                  )}
                </div>
              )}

              {/* ID & Docs tab */}
              {agrTab === 'id' && (
                <div>
                  {[
                    ['Driving Licence', agr.driving_licence_path, 'driving_licence'],
                    ['Passport',        agr.passport_path,        'passport'],
                  ].map(([label, path, docType]) => (
                    <div key={label} style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 0', borderBottom:'1px solid #f3f4f6' }}>
                      <FileText size={16} style={{ color: path ? '#10b981' : '#9ca3af' }} />
                      <span style={{ flex:1, fontSize:14, color:'#374151' }}>{label}</span>
                      <span style={{ fontSize:13, color: path ? '#10b981' : '#9ca3af' }}>
                        {path ? '✓ Uploaded' : 'Not uploaded'}
                      </span>
                      {path && (
                        <div style={{ display:'flex', gap:8 }}>
                          <button
                            onClick={() => {
                              const t = localStorage.getItem('token');
                              fetch(`${API}/api/agreements/download-id/${profileId}/${docType}`, {
                                headers: { Authorization: `Bearer ${t}` }
                              }).then(r => r.blob()).then(blob => {
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${label.replace(' ','_')}_${intro.name?.replace(/[^a-zA-Z0-9]/g,'_')}.pdf`;
                                a.click();
                                URL.revokeObjectURL(url);
                              });
                            }}
                            style={{ padding:'5px 12px', fontSize:12, background:'#3b82f6', color:'#fff', border:'none', borderRadius:4, cursor:'pointer' }}>
                            📥 Download
                          </button>
                          <button onClick={() => setShowReplaceForm(true)}
                            style={{ padding:'5px 12px', fontSize:12, background:'#f59e0b', color:'#fff', border:'none', borderRadius:4, cursor:'pointer' }}>
                            🔄 Replace
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {(agr.id_upload_skipped || !agr.driving_licence_path || !agr.passport_path || showReplaceForm) && !idSuccess && (
                    <div style={{ marginTop:16, padding:14, background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:8 }}>
                      <p style={{ margin:'0 0 6px', fontSize:13, fontWeight:700, color:'#92400e' }}>⚠️ Upload ID on behalf of introducer</p>
                      <p style={{ margin:'0 0 12px', fontSize:12, color:'#78350f' }}>Upload driving licence and/or passport below.</p>
                      {[['Driving Licence', dlFile, setDlFile], ['Passport', ppFile, setPpFile]].map(([lbl, file, setFile]) => (
                        <div key={lbl}>
                          <label style={{ fontSize:12, fontWeight:600, color:'#374151', display:'block', marginBottom:4 }}>{lbl}</label>
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])}
                            style={{ fontSize:12, marginBottom:4, display:'block', width:'100%' }} />
                          {file && <p style={{ fontSize:11, color:'#10b981', margin:'0 0 8px' }}>✓ {file.name}</p>}
                        </div>
                      ))}
                      {idError && <p style={{ fontSize:12, color:'#dc2626', margin:'0 0 8px' }}>{idError}</p>}
                      <button onClick={uploadID} disabled={idUploading}
                        style={{ width:'100%', padding:'9px', borderRadius:8, border:'none', background:'#f59e0b', color:'#fff', fontWeight:600, fontSize:13, cursor:'pointer' }}>
                        {idUploading ? 'Uploading...' : '⬆ Upload Documents'}
                      </button>
                      {showReplaceForm && (
                        <button onClick={() => { setShowReplaceForm(false); setDlFile(null); setPpFile(null); setIdError(''); }}
                          style={{ width:'100%', marginTop:8, padding:'9px', borderRadius:8, border:'1px solid #d1d5db', background:'transparent', color:'#374151', fontWeight:600, fontSize:13, cursor:'pointer' }}>
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                  {idSuccess && <p style={{ fontSize:13, color:'#10b981', marginTop:12, fontWeight:600 }}>{idSuccess}</p>}
                </div>
              )}

            </div>
          </div>
        )}

        {/* ── Relationship notes ──────────────────────────────────────── */}
        <div style={{ ...card, marginBottom:24 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
            <h3 style={{ ...cardTitle, marginBottom:0 }}>Notes ({notes.length})</h3>
            <span style={{ fontSize:11, color:'#94a3b8', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:12, padding:'2px 10px' }}>
              🔒 Internal — never visible to the introducer
            </span>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'flex-start', margin:'14px 0 6px' }}>
            <textarea
              rows={2}
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder="e.g. Met at the Henley networking event — prefers a call over email. Chase for referrals quarterly."
              style={{ flex:1, padding:'10px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }}
            />
            <button onClick={addNote} disabled={noteSaving || !newNote.trim()}
              style={{ ...btn('#10b981','#fff'), opacity: (noteSaving || !newNote.trim()) ? 0.5 : 1, whiteSpace:'nowrap' }}>
              {noteSaving ? 'Saving…' : 'Add note'}
            </button>
          </div>
          {noteError && <p style={{ fontSize:12, color:'#dc2626', margin:'4px 0' }}>{noteError}</p>}
          {notes.length === 0 ? (
            <p style={{ fontSize:13, color:'#94a3b8', textAlign:'center', padding:'18px 0 8px' }}>
              No notes yet — keep track of how you met, their preferences, and when to follow up.
            </p>
          ) : (
            <div style={{ marginTop:10 }}>
              {notes.map((n, i) => (
                <div key={n.id} style={{ padding:'12px 0', borderBottom: i < notes.length-1 ? '1px solid #f7f7f7' : 'none' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
                    <p style={{ margin:0, fontSize:13.5, color:'#1a2e4a', lineHeight:1.6, whiteSpace:'pre-wrap', flex:1 }}>{n.body}</p>
                    <button onClick={() => deleteNote(n.id)} title="Delete note"
                      style={{ background:'none', border:'none', color:'#cbd5e1', cursor:'pointer', fontSize:14, padding:'0 2px', flexShrink:0 }}>🗑</button>
                  </div>
                  <p style={{ margin:'5px 0 0', fontSize:11.5, color:'#94a3b8' }}>
                    {n.author_name || 'Team member'} · {new Date(n.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Deals table ─────────────────────────────────────────────── */}
        <div style={{ ...card, marginBottom:24 }}>
          <h3 style={cardTitle}>Deals ({deals.length})</h3>
          {deals.length === 0 ? (
            <p style={{ fontSize:13, color:'#94a3b8', textAlign:'center', padding:'24px 0' }}>No deals yet</p>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ borderBottom:'2px solid #f0f0f0' }}>
                    {['Client','Commission','Stage','Date',''].map(h => (
                      <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:600, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {deals.map(deal => (
                    <tr key={deal.id} style={{ borderBottom:'1px solid #f7f7f7' }}>
                      <td style={{ padding:'10px 12px', color:'#0e1b2c', fontWeight:500 }}>{deal.client_name}</td>
                      <td style={{ padding:'10px 12px', color:'#10b981', fontWeight:600 }}>£{safeNum(deal.actual_fee).toFixed(2)}</td>
                      <td style={{ padding:'10px 12px' }}>
                        <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20,
                          background: deal.stage === 'completed' ? '#d1fae5' : '#f1f5f9',
                          color: deal.stage === 'completed' ? '#065f46' : '#374151' }}>
                          {deal.stage}
                        </span>
                      </td>
                      <td style={{ padding:'10px 12px', color:'#94a3b8' }}>{new Date(deal.created_at).toLocaleDateString('en-GB')}</td>
                      <td style={{ padding:'10px 12px' }}>
                        <button onClick={() => navigate(`/deal/${deal.id}`)}
                          style={btn('#f7fafc','#374151','1px solid #e2e8f0','6px 10px','11px')}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Edit Modal ──────────────────────────────────────────────── */}
        {editModal && (
          <div style={overlay}>
            <div style={modal}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:'#0e1b2c' }}>Edit Introducer</h3>
                <button onClick={() => setEditModal(false)} style={{ background:'none', border:'none', fontSize:20, color:'#94a3b8', cursor:'pointer' }}>×</button>
              </div>

              <label style={lbl}>Name</label>
              <input style={inp} value={editForm.name}
                onChange={e => setEditForm(f=>({...f,name:e.target.value}))} />

              <label style={lbl}>Email</label>
              <input style={inp} type="email" value={editForm.email}
                onChange={e => setEditForm(f=>({...f,email:e.target.value}))} />
              <p style={{ fontSize:11.5, color:'#94a3b8', margin:'-8px 0 14px' }}>
                ⚠️ This is also their login email — changing it changes how they sign in.
              </p>

              <label style={lbl}>Contact Phone</label>
              <input style={inp} type="tel" value={editForm.contact_phone}
                onChange={e => setEditForm(f=>({...f,contact_phone:e.target.value}))} />

              <label style={lbl}>Introducing As</label>
              <div style={{ display:'flex', gap:8, marginBottom:14 }}>
                {['individual','company'].map(t => (
                  <button key={t} onClick={() => setEditForm(f=>({...f,entity_type:t}))}
                    style={{ flex:1, padding:'8px', borderRadius:8,
                      border: `1px solid ${editForm.entity_type===t ? '#10b981':'#e2e8f0'}`,
                      background: editForm.entity_type===t ? '#10b981':'#fff',
                      color: editForm.entity_type===t ? '#fff':'#374151',
                      cursor:'pointer', fontSize:13 }}>
                    {t === 'individual' ? 'Individual' : 'Company'}
                  </button>
                ))}
              </div>

              {editForm.entity_type === 'company' && (
                <>
                  <label style={lbl}>Company Name</label>
                  <input style={inp} value={editForm.company_name}
                    onChange={e => setEditForm(f=>({...f,company_name:e.target.value}))} />

                  <label style={lbl}>Company Address (optional)</label>
                  <input style={inp} value={editForm.company_address}
                    onChange={e => setEditForm(f=>({...f,company_address:e.target.value}))} />
                </>
              )}

              <label style={lbl}>Fee Type</label>
              <div style={{ display:'flex', gap:8, marginBottom:14 }}>
                {['percentage','fixed'].map(t => (
                  <button key={t} onClick={() => setEditForm(f=>({...f,fee_type:t}))}
                    style={{ flex:1, padding:'8px', borderRadius:8,
                      border: `1px solid ${editForm.fee_type===t ? '#10b981':'#e2e8f0'}`,
                      background: editForm.fee_type===t ? '#10b981':'#fff',
                      color: editForm.fee_type===t ? '#fff':'#374151',
                      cursor:'pointer', fontSize:13 }}>
                    {t === 'percentage' ? '% of Commission' : 'Fixed Fee'}
                  </button>
                ))}
              </div>

              <label style={lbl}>{editForm.fee_type === 'percentage' ? 'Percentage (%)' : 'Fixed Fee (£)'}</label>
              <input style={inp} type="text" value={editForm.fee_value}
                onChange={e => setEditForm(f=>({...f,fee_value:e.target.value.replace(/[^0-9.]/g,'')}))} />

              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
                <input type="checkbox" id="renewals" checked={editForm.fee_applies_to_renewals}
                  onChange={e => setEditForm(f=>({...f,fee_applies_to_renewals:e.target.checked}))} />
                <label htmlFor="renewals" style={{ fontSize:13, color:'#374151' }}>Fee applies to renewals</label>
              </div>

              <label style={lbl}>Adviser Name</label>
              <input style={inp} value={editForm.adviser_name}
                onChange={e => setEditForm(f=>({...f,adviser_name:e.target.value}))} />

              <label style={lbl}>FRN Number</label>
              <input style={inp} value={editForm.frn_number}
                onChange={e => setEditForm(f=>({...f,frn_number:e.target.value}))} />

              {saveMsg && <p style={{ fontSize:13, color: saveMsg.includes('✓') ? '#10b981':'#dc2626', marginBottom:8 }}>{saveMsg}</p>}

              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
                <button onClick={() => setEditModal(false)} style={btn('#f1f5f9','#374151')}>Cancel</button>
                <button onClick={handleSave} disabled={saving} style={btn('#10b981','#fff')}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Archive confirm ─────────────────────────────────────────── */}
        {confirmDel && (
          <div style={overlay}>
            <div style={{ ...modal, maxWidth:400 }}>
              <h3 style={{ margin:'0 0 12px', color:'#0e1b2c' }}>Archive Introducer?</h3>
              <p style={{ fontSize:13, color:'#718096', marginBottom:20 }}>
                This will archive {intro.name} and hide them from active lists. Their deals and history are preserved.
              </p>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button onClick={() => setConfirmDel(false)} style={btn('#f1f5f9','#374151')}>Cancel</button>
                <button onClick={handleDelete} style={btn('#dc2626','#fff')}>Archive</button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

// ── Dynamic personal data viewer ──────────────────────────────────────────────
function DynamicDataView({ formData, agreement }) {
  if (!formData) return <p style={{ fontSize:13, color:'#9ca3af' }}>No form data available.</p>;

  const FIRM_KEYS    = ['firm_name','introducing_firm_name','firm_address','introducing_firm_address','firm_telephone_number','firm_fax_number','nature_of_business','fax_number'];
  const EXCLUDE_KEYS = ['additional_details','additional_information','additional_details_instruction'];

  const personalEntries = Object.entries(formData).filter(([key]) => {
    const k = key.toLowerCase();
    if (FIRM_KEYS.some(fk => k.includes(fk))) return false;
    if (EXCLUDE_KEYS.some(ek => k.includes(ek))) return false;
    if (['yes','no'].includes(String(formData[key]).toLowerCase()) && key.length > 10) return false;
    return true;
  });

  return (
    <div>
      <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8, padding:'12px 16px', marginBottom:16, display:'flex', gap:24, flexWrap:'wrap' }}>
        <div>
          <p style={{ fontSize:11, color:'#9ca3af', margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>Signed As</p>
          <p style={{ fontSize:15, fontStyle:'italic', color:'#1a365d', fontWeight:600, margin:0 }}>{agreement.esign_name}</p>
        </div>
        <div>
          <p style={{ fontSize:11, color:'#9ca3af', margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>Date</p>
          <p style={{ fontSize:13, color:'#065f46', margin:0 }}>{new Date(agreement.signed_at).toLocaleString('en-GB')}</p>
        </div>
        <div>
          <p style={{ fontSize:11, color:'#9ca3af', margin:'0 0 2px', textTransform:'uppercase', fontWeight:600 }}>IP Address</p>
          <p style={{ fontSize:13, color:'#065f46', margin:0 }}>{agreement.ip_address}</p>
        </div>
      </div>
      {personalEntries.map(([key, value]) => {
        const label   = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const display = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value || '—');
        return (
          <div key={key} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
            padding:'8px 0', borderBottom:'1px solid #f3f4f6', gap:12 }}>
            <span style={{ fontSize:12, color:'#9ca3af', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', flex:'0 0 45%' }}>{label}</span>
            <span style={{ fontSize:13, color:'#0e1b2c', textAlign:'right', flex:1 }}>{display}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Dynamic firm data viewer ──────────────────────────────────────────────────
function DynamicFirmView({ formData }) {
  if (!formData) return <p style={{ fontSize:13, color:'#9ca3af' }}>No firm data available.</p>;

  const FIRM_KEYS = ['firm_name','introducing_firm_name','firm_address','introducing_firm_address',
    'firm_telephone','firm_fax','fax_number','nature_of_business','firm_telephone_number','firm_fax_number'];

  const firmEntries = Object.entries(formData).filter(([key]) => {
    const k = key.toLowerCase();
    return FIRM_KEYS.some(fk => k.includes(fk));
  });

  if (firmEntries.length === 0) return <p style={{ fontSize:13, color:'#9ca3af' }}>No firm details recorded.</p>;

  return (
    <div>
      {firmEntries.map(([key, value]) => {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return (
          <div key={key} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f3f4f6', gap:12 }}>
            <span style={{ fontSize:12, color:'#9ca3af', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em', flex:'0 0 45%' }}>{label}</span>
            <span style={{ fontSize:13, color:'#0e1b2c', textAlign:'right', flex:1 }}>{value || '—'}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
function InfoRow({ label, value }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #f7f7f7' }}>
      <span style={{ fontSize:11, fontWeight:600, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</span>
      <span style={{ fontSize:13, color:'#0e1b2c', textAlign:'right', maxWidth:'60%' }}>{value || '—'}</span>
    </div>
  );
}

// ── Style helpers ─────────────────────────────────────────────────────────────
const btn = (bg, color, border='none', padding='8px 16px', fontSize='13px') => ({
  padding, fontSize, borderRadius:8, border, background:bg, color,
  cursor:'pointer', fontWeight:600, display:'inline-flex', alignItems:'center', gap:6,
});
const card      = { background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:20 };
const cardTitle = { margin:'0 0 14px', fontSize:14, fontWeight:700, color:'#0e1b2c' };
const overlay   = { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 };
const modal     = { background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:480, maxHeight:'90vh', overflowY:'auto' };
const lbl       = { display:'block', fontSize:12, fontWeight:600, color:'#374151', marginBottom:4 };
const inp       = { width:'100%', padding:'10px 12px', borderRadius:8, border:'1px solid #e2e8f0', fontSize:13, boxSizing:'border-box', marginBottom:14 };
